import constants from 'app/constants'
import { arrayDifference } from 'lib/util/array'
import { removeSpecialCharacters } from 'lib/util/string'
import parseColorGroup from './parseColorGroup'

/**
 * Get the brands available from the given categories
 *
 * @param {object} props - the input props
 * @returns {Array} the array of brands
 */
const getBrands = props => {
  const { brandList, brands: BRANDS, categories, type = 'all' } = props

  const items = Array.isArray(categories)
    ? categories.flatMap(({ items }) => items)
    : Object.values(categories).flatMap(Object.values)

  const initialBrands = brandList.reduce((res, key) => {
    res[key.toLowerCase()] = 0

    return res
  }, {})

  const brands = items.reduce(
    (res, item) => {
      const createdBy = removeSpecialCharacters(
        (item.createdBy || '').toLowerCase()
      )

      res[createdBy] = (res[createdBy] || 0) + 1

      return res
    },
    { ...initialBrands }
  )

  const filteredBrands = Object.keys(brands).filter(key => {
    if (!BRANDS?.[key]) {
      console.warn('No brand data found for:', key)

      return false
    }

    if (!BRANDS?.[key].is_available) {
      // Skip unavailable brands (brands whose products are no longer for sale, E.g. limited run)
      return false
    }

    return true
  })

  const res = filteredBrands.sort().reduce((result, key) => {
    const match = BRANDS?.[key]

    if (match.type === type || type === 'all') {
      result.push({ count: brands[key], ...match })
    }

    return result
  }, [])

  return res
}

/**
 * Filter categories
 *
 * @param {object} props - the input props
 * @returns {Array} the filtered categories
 */
export const filterCategories = props => {
  const { categories, categoryConfig, ids } = props
  const filtered = ids.reduce((res, id) => {
    const match = categories[id]

    if (match && match.length) {
      const config = categoryConfig[id]

      res.push({
        id,
        label: config?.label,
        isRequired: config?.isRequired || false,
        items: match
      })
    }

    return res
  }, [])

  return filtered
}

/**
 * Create a bucket
 *
 * @param {object} props - the input props
 * @returns {object} the bucket data
 */
function createBucket (props) {
  const { brandList, brands, categories, categoryConfig, config } = props
  const { gender, type, style = 'puks', excludes = [] } = config
  const filterCategory = item =>
    (item.style.includes(style) || !item.style.length) &&
    (item.type === type || !item.type) &&
    (item.gender.includes(gender) || !item.gender.length)

  // Exclude the 'excludes' from the categories, then filter
  const filtered = arrayDifference(Object.keys(categories), excludes).reduce(
    (res, key) => {
      res[key] = categories[key].filter(filterCategory)

      return res
    },
    {}
  )

  if (filtered.ears && filtered.ears.length) {
    filtered.ears = parseColorGroup(filtered.ears)
  }

  const baseCategories = filterCategories({
    ids: constants.AVATAR_BASE_CATEGORIES,
    categories: filtered,
    categoryConfig
  })

  const productCategories = filterCategories({
    ids: constants.AVATAR_PRODUCT_CATEGORIES,
    categories: filtered,
    categoryConfig
  })

  return {
    baseCategories,
    baseBrands: getBrands({
      categories: baseCategories,
      type: 'brand',
      brands,
      brandList
    }),
    baseCreators: getBrands({
      categories: baseCategories,
      type: 'creator',
      brands,
      brandList
    }),
    baseCommunity: getBrands({
      categories: baseCategories,
      type: 'community',
      brands,
      brandList
    }),
    productCategories,
    productBrands: getBrands({
      categories: productCategories,
      type: 'brand',
      brands,
      brandList
    }),
    productCreators: getBrands({
      categories: productCategories,
      type: 'creator',
      brands,
      brandList
    }),
    productCommunity: getBrands({
      categories: productCategories,
      type: 'community',
      brands,
      brandList
    }),
    categories: filtered,
    categoriesBrands: getBrands({
      categories: filtered,
      type: 'all',
      brands,
      brandList
    })
  }
}

/**
 * Parse categories into buckets
 *
 * @param {object} props - the input props
 * @returns {object} the parsed buckets
 */
export default function parseBuckets (props) {
  const { brandBuckets, brands, categories, categoryConfig } = props
  const res = {
    citizen: {
      male: {
        puks: createBucket({
          categories,
          config: { type: 'citizen', gender: 'male', style: 'puks' },
          categoryConfig,
          brands,
          brandList: brandBuckets?.citizen?.male || []
        }),
        manga: createBucket({
          categories,
          config: { type: 'citizen', gender: 'male', style: 'manga' },
          categoryConfig,
          brands,
          brandList: brandBuckets?.citizen?.male || []
        })
      },
      female: {
        puks: createBucket({
          categories,
          config: {
            type: 'citizen',
            gender: 'female',
            style: 'puks'
          },
          categoryConfig,
          brands,
          brandList: brandBuckets?.citizen?.female || []
        }),
        manga: createBucket({
          categories,
          config: {
            type: 'citizen',
            gender: 'female',
            style: 'manga'
          },
          categoryConfig,
          brands,
          brandList: brandBuckets?.citizen?.female || []
        })
      },
      hybrid_male: {
        puks: createBucket({
          categories,
          config: {
            type: 'citizen',
            gender: 'hybrid_male',
            style: 'puks',
            excludes: ['ears'] // exclude the ear category
          },
          categoryConfig,
          brands,
          brandList: brandBuckets?.citizen?.hybrid_male || []
        })
      },
      hybrid_female: {
        puks: createBucket({
          categories,
          config: {
            type: 'citizen',
            gender: 'hybrid_female',
            style: 'puks',
            excludes: ['ears'] // exclude the ear category
          },
          categoryConfig,
          brands,
          brandList: brandBuckets?.citizen?.hybrid_female || []
        })
      }
    },
    nomad: {
      et: {
        puks: createBucket({
          categories,
          config: {
            type: 'nomad',
            gender: 'et',
            style: 'puks'
          },
          categoryConfig,
          brands,
          brandList: brandBuckets?.nomad?.ET || []
        })
      },
      cyborg_male: {
        puks: createBucket({
          categories,
          config: {
            type: 'nomad',
            gender: 'cyborg_male',
            style: 'puks'
          },
          categoryConfig,
          brands,
          brandList: brandBuckets?.nomad?.cyborg_male || []
        })
      },
      cyborg_female: {
        puks: createBucket({
          categories,
          config: {
            type: 'nomad',
            gender: 'cyborg_female',
            style: 'puks'
          },
          categoryConfig,
          brands,
          brandList: brandBuckets?.nomad?.cyborg_female || []
        })
      }
    }
  }

  return res
}
